import { getSetApiData } from "@/services/api-service/get-set-api-data";
import { EHttpMethod } from "@/services/api-service/enums";
import { ICallAndSetData } from "@/services/api-service/interface";
import {
  IAddCustomerSkusRequest,
  ICreateQuoteFromSkuReorderRequest,
  IGetAllCustomerSkusRequest,
  IUpdateCustomerSkuDateRequest,
  IUpdateCustomerSkuSerialNumberRequest,
} from "@/services/api-service/customer-skus/customer-skus.types";

const ADD_CUSTOMER_SKUS_URL =
  "/v1/jobs-service/customer-skus/add-customer-skus";
const GET_ALL_CUSTOMER_SKUS_URL =
  "/v1/jobs-service/customer-skus/get-all-customer-skus";
const GET_SKUS_BY_BOARD_MISSION_ID_URL =
  "/v1/jobs-service/customer-skus/get-skus-by-board-mission-id";
const GET_DEFAULT_CUSTOMER_SKUS_BY_BOARD_MISSION_ID_URL =
  "/v1/jobs-service/customer-skus/get-default-customer-skus-by-board-mission-id";
const GET_DEFAULT_CUSTOMER_SKUS_BY_CUSTOMER_ID_URL =
  "/v1/jobs-service/customer-skus/get-default-customer-skus-by-customer-id";
const CHECK_CUSTOMER_SKU_EXISTS_URL =
  "/v1/jobs-service/customer-skus/check-customer-sku-exists";
const CREATE_QUOTE_FROM_SKU_REORDER_URL =
  "/v1/erp-service/documents/create-quote-from-reorder";
const DELETE_CUSTOMER_SKU_URL =
  "/v1/jobs-service/customer-skus/delete-customer-sku";
const DELETE_CUSTOMER_SKUS_BY_GROUP_NAME_URL =
  "/v1/jobs-service/customer-skus/by-sku-group-name";
const UPDATE_CUSTOMER_SKU_EXPIRATION_DATE_URL =
  "/v1/jobs-service/customer-skus/update-customer-sku-expiration-date";
const UPDATE_CUSTOMER_SKU_SERIAL_NUMBER_URL =
  "/v1/jobs-service/customer-skus/update-customer-sku-serial-number";
const UPDATE_CUSTOMER_SKU_START_DATE_URL =
  "/v1/jobs-service/customer-skus/update-customer-sku-start-date";

const addCustomerSkusApi: ICallAndSetData = async (
  callApi,
  setState,
  data: IAddCustomerSkusRequest
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.POST,
    ADD_CUSTOMER_SKUS_URL,
    setState,
    data
  );
};

const getAllCustomerSkusApi: ICallAndSetData = async (
  callApi,
  setState,
  data?: IGetAllCustomerSkusRequest
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.POST,
    GET_ALL_CUSTOMER_SKUS_URL,
    setState,
    data
  );
};

const getSkusByBoardMissionIdApi: ICallAndSetData = async (
  callApi,
  setState,
  boardMissionId: string
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${GET_SKUS_BY_BOARD_MISSION_ID_URL}/${boardMissionId}`,
    setState
  );
};

const getDefaultCustomerSkusByBoardMissionIdApi: ICallAndSetData = async (
  callApi,
  setState,
  boardMissionId: string
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${GET_DEFAULT_CUSTOMER_SKUS_BY_BOARD_MISSION_ID_URL}/${boardMissionId}`,
    setState
  );
};

const getDefaultCustomerSkusByCustomerIdApi: ICallAndSetData = async (
  callApi,
  setState,
  customerId: string
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${GET_DEFAULT_CUSTOMER_SKUS_BY_CUSTOMER_ID_URL}/${encodeURIComponent(
      customerId
    )}`,
    setState
  );
};

const checkCustomerSkuExistsApi: ICallAndSetData = async (
  callApi,
  setState,
  data: { customerId: string; sku: string }
) => {
  const customerId = encodeURIComponent(data?.customerId || "");
  const sku = encodeURIComponent(data?.sku || "");
  return await getSetApiData(
    callApi,
    EHttpMethod.GET,
    `${CHECK_CUSTOMER_SKU_EXISTS_URL}/${customerId}?sku=${sku}`,
    setState,
    undefined,
    false
  );
};

const createQuoteFromSkuReorderApi: ICallAndSetData = async (
  callApi,
  setState,
  data: ICreateQuoteFromSkuReorderRequest
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.POST,
    CREATE_QUOTE_FROM_SKU_REORDER_URL,
    setState,
    data
  );
};

const deleteCustomerSkuApi: ICallAndSetData = async (
  callApi,
  setState,
  id: string
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.DELETE,
    `${DELETE_CUSTOMER_SKU_URL}/${encodeURIComponent(id)}`,
    setState
  );
};

const updateCustomerSkuExpirationDateApi: ICallAndSetData = async (
  callApi,
  setState,
  data: IUpdateCustomerSkuDateRequest
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.PUT,
    UPDATE_CUSTOMER_SKU_EXPIRATION_DATE_URL,
    setState,
    data,
    false
  );
};

const updateCustomerSkuSerialNumberApi: ICallAndSetData = async (
  callApi,
  setState,
  data: IUpdateCustomerSkuSerialNumberRequest
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.PUT,
    UPDATE_CUSTOMER_SKU_SERIAL_NUMBER_URL,
    setState,
    data,
    false
  );
};

const updateCustomerSkuStartDateApi: ICallAndSetData = async (
  callApi,
  setState,
  data: IUpdateCustomerSkuDateRequest
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.PUT,
    UPDATE_CUSTOMER_SKU_START_DATE_URL,
    setState,
    data,
    false
  );
};

const deleteCustomerSkusByGroupNameApi: ICallAndSetData = async (
  callApi,
  setState,
  skuGroupName: string
) => {
  return await getSetApiData(
    callApi,
    EHttpMethod.DELETE,
    `${DELETE_CUSTOMER_SKUS_BY_GROUP_NAME_URL}/${encodeURIComponent(
      skuGroupName
    )}`,
    setState
  );
};

export {
  ADD_CUSTOMER_SKUS_URL,
  GET_ALL_CUSTOMER_SKUS_URL,
  GET_SKUS_BY_BOARD_MISSION_ID_URL,
  GET_DEFAULT_CUSTOMER_SKUS_BY_BOARD_MISSION_ID_URL,
  GET_DEFAULT_CUSTOMER_SKUS_BY_CUSTOMER_ID_URL,
  CHECK_CUSTOMER_SKU_EXISTS_URL,
  CREATE_QUOTE_FROM_SKU_REORDER_URL,
  DELETE_CUSTOMER_SKU_URL,
  DELETE_CUSTOMER_SKUS_BY_GROUP_NAME_URL,
  UPDATE_CUSTOMER_SKU_EXPIRATION_DATE_URL,
  UPDATE_CUSTOMER_SKU_SERIAL_NUMBER_URL,
  UPDATE_CUSTOMER_SKU_START_DATE_URL,
  addCustomerSkusApi,
  getAllCustomerSkusApi,
  getSkusByBoardMissionIdApi,
  getDefaultCustomerSkusByBoardMissionIdApi,
  getDefaultCustomerSkusByCustomerIdApi,
  checkCustomerSkuExistsApi,
  createQuoteFromSkuReorderApi,
  deleteCustomerSkuApi,
  deleteCustomerSkusByGroupNameApi,
  updateCustomerSkuExpirationDateApi,
  updateCustomerSkuSerialNumberApi,
  updateCustomerSkuStartDateApi,
};
